import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { API, api } from "./api";

const KEY = "aegis-token";
export const getToken = () => sessionStorage.getItem("aegis-embed-token") || localStorage.getItem(KEY);
export const authDownload = async (url, filename) => {
  try {
    const r = await api.get(url.startsWith(API) ? url.slice(API.length) : url, { responseType: "blob" });
    const name = filename || (r.headers["content-disposition"] || "").match(/filename="?([^";]+)/)?.[1] || url.split("/").pop();
    const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = name; a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 5000);
  } catch (e) { toast.error("Download failed. Please try again."); }
};

export const apiError = (e, fallback = "Something went wrong. Please try again.") => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  return e?.message || fallback;
};

const onUnauthorized = () => {
  localStorage.removeItem(KEY);
  const p = window.location.pathname;
  if (p.startsWith("/app")) window.location.assign(`/login?next=${encodeURIComponent(p)}`);
};
const isAuthUrl = (u) => u.includes("/api/auth/");

const nativeFetch = window.fetch.bind(window);
window.fetch = (input, init = {}) => {
  const url = typeof input === "string" ? input : input.url;
  const ours = url.startsWith(API);
  if (ours && getToken()) {
    const h = new Headers(init.headers || {});
    if (!h.has("Authorization")) h.set("Authorization", `Bearer ${getToken()}`);
    init = { ...init, headers: h };
  }
  return nativeFetch(input, init).then((r) => {
    if (ours && r.status === 401 && !isAuthUrl(url)) onUnauthorized();
    if (ours && r.status === 403) r.clone().json().then((j) => toast.error(j.detail || "Not allowed for your role")).catch(() => {});
    return r;
  });
};

api.interceptors.request.use((c) => {
  const t = getToken();
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});
api.interceptors.response.use((r) => r, (e) => {
  const s = e.response?.status;
  if (s === 401 && !isAuthUrl(e.config?.url || "")) onUnauthorized();
  if (s === 403) toast.error(apiError(e));
  return Promise.reject(e);
});

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(getToken() ? null : false);
  useEffect(() => {
    if (!getToken()) return;
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => { localStorage.removeItem(KEY); setUser(false); });
  }, []);
  const signIn = useCallback(({ token, user: u, device_token }) => { localStorage.setItem(KEY, token); if (device_token) localStorage.setItem("aegis-device", device_token); setUser(u); }, []);
  const signOut = useCallback(() => { api.post("/auth/logout").catch(() => {}); localStorage.removeItem(KEY); setUser(false); }, []);
  const role = user?.role;
  return (
    <AuthCtx.Provider value={{ user, setUser, signIn, signOut, isOwner: role === "owner", isAdmin: !!user?.is_platform_admin, canWrite: role === "owner" || role === "analyst" }}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => useContext(AuthCtx);

export const RequireAuth = ({ children }) => {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <div className="min-h-screen bg-canvas grid place-items-center font-mono text-xs text-white/40" data-testid="auth-checking">VERIFYING SESSION…</div>;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname)}`} replace />;
  return children;
};

export const RequireAdmin = ({ children }) => {
  const { isAdmin } = useAuth();
  return isAdmin ? children : <Navigate to="/app" replace />;
};

export const RequireOwner = ({ children }) => {
  const { isOwner } = useAuth();
  return isOwner ? children : <Navigate to="/app" replace />;
};
